import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { getMarketingT } from "../utils/marketingTranslations";
import MarketingLayout from "../components/marketing/MarketingLayout";

function PricingPage() {
  const { language } = useApp();
  const mt = getMarketingT(language);
  const isRTL = language === "ar";
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const PLANS = [
    {
      name: mt("plan_main"),
      desc: mt("plan_main_desc"),
      highlighted: true,

      pricing: [
        {
          duration: mt("plan_3_months"),
          price: "599",
        },
        {
          duration: mt("plan_6_months"),
          price: "999",
          badge: mt("plan_save_10"),
        },
        {
          duration: mt("plan_12_months"),
          price: "1799",
          badge: mt("plan_best_value"),
        },
      ],

      features: [
        mt("plan_main_f1"),
        mt("plan_main_f2"),
        mt("plan_main_f3"),
        mt("plan_main_f4"),
        mt("plan_main_f5"),
      ],

      cta: mt("plan_get_started"),
      ctaLink: "/contact",
    },
  ];

  const FAQS = [
    { q: mt("faq_1_q"), a: mt("faq_1_a") },
    { q: mt("faq_2_q"), a: mt("faq_2_a") },
    { q: mt("faq_3_q"), a: mt("faq_3_a") },
    { q: mt("faq_4_q"), a: mt("faq_4_a") },
  ];

  return (
    <MarketingLayout>
      <style>{`
        @keyframes mk-fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .mk-pricing-hero {
          padding: 140px 24px 60px; text-align: center;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-light);
        }
        .mk-plan-card {
          padding: 40px 32px; border-radius: var(--radius-lg);
          background: var(--bg-secondary); border: 1px solid var(--border-light);
          display: flex; flex-direction: column; transition: all 0.3s; position: relative;
        }
        .mk-plan-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
        .mk-plan-highlighted { border: 2px solid var(--accent); box-shadow: 0 20px 60px var(--accent-glow); }
        .mk-plan-badge {
          position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
          padding: 6px 20px; background: var(--accent); color: var(--text-inverse);
          border-radius: 100px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
          white-space: nowrap;
        }
        [dir="rtl"] .mk-plan-badge { left: auto; right: 50%; transform: translateX(50%); }
        .mk-plan-feature {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 8px 0; font-size: 14px; color: var(--text-secondary);
        }
        .mk-plan-check {
          width: 18px; height: 18px; border-radius: 50%;
          background: rgba(76, 175, 80, 0.12); color: var(--success);
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 700; flex-shrink: 0; margin-top: 1px;
        }
        .mk-plan-cta {
          display: block; text-align: center; padding: 14px 24px;
          border-radius: var(--radius-md); text-decoration: none;
          font-size: 15px; font-weight: 600; transition: all 0.3s; margin-top: auto;
        }
        .mk-plan-cta-primary { background: var(--accent); color: var(--text-inverse); box-shadow: 0 4px 16px var(--accent-glow); }
        .mk-plan-cta-primary:hover { opacity: 0.9; transform: translateY(-2px); }
        .mk-plan-cta-secondary { background: var(--bg-tertiary); color: var(--text-primary); }
        .mk-plan-cta-secondary:hover { background: var(--border); }

        .mk-faq-item { border-bottom: 1px solid var(--border-light); cursor: pointer; }
        .mk-faq-q {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 0; font-size: 16px; font-weight: 500; color: var(--text-primary);
        }
        .mk-faq-a { font-size: 14px; color: var(--text-muted); line-height: 1.7; padding-bottom: 20px; }
        .mk-faq-toggle {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0; transition: all 0.3s;
        }
        @media (max-width: 768px) {
          .mk-plans-grid { grid-template-columns: 1fr !important; max-width: 420px; margin: 0 auto !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .mk-plans-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* Hero */}
      <section className="mk-pricing-hero">
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--accent)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 16,
            animation: "mk-fadeUp 0.6s ease both",
          }}
        >
          {mt("pricing_label")}
        </p>
        <h1
          style={{
            fontFamily: isRTL ? "var(--font-arabic)" : "var(--font-display)",
            fontSize: "clamp(32px, 5vw, 52px)",
            fontWeight: 700,
            color: "var(--text-primary)",
            letterSpacing: isRTL ? "0" : "-0.03em",
            marginBottom: 20,
            animation: "mk-fadeUp 0.6s ease 0.1s both",
          }}
        >
          {mt("pricing_title")}
        </h1>
        <p
          style={{
            fontSize: 18,
            color: "var(--text-muted)",
            lineHeight: 1.7,
            maxWidth: 480,
            margin: "0 auto",
            animation: "mk-fadeUp 0.6s ease 0.2s both",
          }}
        >
          {mt("pricing_sub")}
        </p>
      </section>

      {/* Plans */}
      <section style={{ padding: "60px 24px 80px" }}>
        <div
          className="mk-plans-grid"
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1fr",
            maxwidth: 500,
            gap: 24,
            alignItems: "start",
          }}
        >
          {PLANS.map((plan, i) => (
            <div
              key={i}
              className={`mk-plan-card ${plan.highlighted ? "mk-plan-highlighted" : ""}`}
            >
              {plan.badge && <div className="mk-plan-badge">{plan.badge}</div>}
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  marginBottom: 8,
                  fontFamily: isRTL ? "var(--font-arabic)" : "var(--font-body)",
                }}
              >
                {plan.name}
              </h3>
              <div style={{ marginBottom: 20 }}>
                {plan.pricing.map((p, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: "1px solid var(--border-light)",
                    }}
                  >
                    {/* Duration */}
                    <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
                      {p.duration}
                    </span>

                    {/* Price */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 12 }}>MAD</span>
                      <span style={{ fontSize: 20, fontWeight: 600 }}>
                        {p.price}
                      </span>

                      {/* Badge */}
                      {p.badge && (
                        <span
                          style={{
                            fontSize: 10,
                            background: "var(--accent)",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                          }}
                        >
                          {p.badge}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: "var(--text-muted)",
                  marginBottom: 28,
                  lineHeight: 1.6,
                }}
              >
                {plan.desc}
              </p>
              <div style={{ marginBottom: 32 }}>
                {plan.features.map((f, j) => (
                  <div key={j} className="mk-plan-feature">
                    <span className="mk-plan-check">✓</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link
                to={plan.ctaLink}
                className={`mk-plan-cta ${plan.highlighted ? "mk-plan-cta-primary" : "mk-plan-cta-secondary"}`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Zero commission */}
      <section style={{ padding: "0 24px 80px" }}>
        <div
          style={{
            maxWidth: 800,
            margin: "0 auto",
            textAlign: "center",
            padding: 48,
            borderRadius: "var(--radius-lg)",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-light)",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 16 }}>💸</div>
          <h3
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: 8,
              fontFamily: isRTL ? "var(--font-arabic)" : "var(--font-body)",
            }}
          >
            {mt("pricing_zero_title")}
          </h3>
          <p
            style={{
              fontSize: 15,
              color: "var(--text-muted)",
              lineHeight: 1.7,
              maxWidth: 500,
              margin: "0 auto",
            }}
          >
            {mt("pricing_zero_desc")}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "0 24px 100px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: isRTL ? "var(--font-arabic)" : "var(--font-display)",
              fontSize: 32,
              fontWeight: 700,
              color: "var(--text-primary)",
              textAlign: "center",
              marginBottom: 48,
            }}
          >
            {mt("faq_title")}
          </h2>
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className="mk-faq-item"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div className="mk-faq-q">
                <span>{faq.q}</span>
                <span
                  className="mk-faq-toggle"
                  style={{
                    transform: openFaq === i ? "rotate(45deg)" : "none",
                    background:
                      openFaq === i
                        ? "var(--accent-light)"
                        : "var(--bg-tertiary)",
                    color:
                      openFaq === i ? "var(--accent)" : "var(--text-muted)",
                  }}
                >
                  +
                </span>
              </div>
              {openFaq === i && <div className="mk-faq-a">{faq.a}</div>}
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}

export default PricingPage;
